import { cn } from "@/lib/utils";
import { forwardRef } from "react";

// ============================================
// 타입 정의
// ============================================

/** Figma: size = 32px / 24px */
export type SwitchSize = 32 | 24;
export type SwitchColor = "orange" | "blue";

interface SwitchProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> {
  size?: SwitchSize;
  /** 활성 상태의 의미색. 기존 검토/설정 토글은 orange가 기본값이다. */
  color?: SwitchColor;
  /** Figma: active = false / true */
  active?: boolean;
  /** Thumb 내부 아이콘 (선택) — Figma 예시는 Σ */
  icon?: React.ReactNode;
  onChange?: (active: boolean) => void;
}

// ============================================
// Figma 디자인 토큰
// ============================================

/** Track 사이즈 (W × H) — Figma: 56×32 / 44×24 */
const trackSizeStyles: Record<SwitchSize, string> = {
  32: "w-14 h-8",
  24: "w-11 h-6",
};

/** Thumb 사이즈 — Figma: 24px / 20px */
const thumbSizeStyles: Record<SwitchSize, string> = {
  32: "size-6",
  24: "size-5",
};

/** Thumb translate-x (Off→On 이동 거리) — Figma: track − thumb − padding×2 */
const thumbTranslateStyles: Record<SwitchSize, string> = {
  32: "translate-x-6", // 56-24-4*2 = 24px
  24: "translate-x-5", // 44-20-2*2 = 20px
};

/** Thumb inset (track 내부 padding) */
const thumbInsetStyles: Record<SwitchSize, string> = {
  32: "left-1", // 4px
  24: "left-0.5", // 2px
};

/** Icon 사이즈 — Figma: thumb 내부 아이콘 (14px / 10px) */
const iconSizeStyles: Record<SwitchSize, string> = {
  32: "size-3.5",
  24: "size-2.5",
};

// ============================================
// 컴포넌트
// ============================================

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      size = 32,
      color = "orange",
      active = false,
      disabled = false,
      icon,
      className,
      onChange,
      onClick,
      ...props
    },
    ref,
  ) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      onChange?.(!active);
      onClick?.(e);
    };

    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={active}
        disabled={disabled}
        onClick={handleClick}
        className={cn(
          "relative inline-flex items-center rounded-full transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
          trackSizeStyles[size],
          // Track 색상: active=의미색, off=neutral/200
          active
            ? color === "blue"
              ? "bg-primary-color"
              : "bg-[#ff6900]"
            : "bg-neutral-200",
          // Disabled: opacity 52%
          disabled && "opacity-52 cursor-not-allowed",
          !disabled && "cursor-pointer",
          className,
        )}
        {...props}
      >
        {/* Thumb */}
        <span
          className={cn(
            "absolute top-1/2 -translate-y-1/2 rounded-full transition-transform flex items-center justify-center",
            thumbSizeStyles[size],
            thumbInsetStyles[size],
            // 파란 정보 토글은 흰 썸으로 대비를 유지한다.
            active && color === "orange" ? "bg-[#ffedd4]" : "bg-static-white",
            active && thumbTranslateStyles[size],
          )}
        >
          {icon && (
            <span
              className={cn(
                "flex items-center justify-center",
                iconSizeStyles[size],
              )}
              aria-hidden
            >
              {icon}
            </span>
          )}
        </span>
      </button>
    );
  },
);

Switch.displayName = "Switch";

export default Switch;
